import { Coach } from 'fitify-types/src/types/coach'
import { UnitSystem, WithId } from 'fitify-types/src/types/common'
import { User } from 'fitify-types/src/types/user'
import {
  clearItem,
  CM_PER_INCH,
  getItem,
  KG_PER_LBS,
  setItem,
} from 'fitify-ui-onboarding/src/utils'
import { createContext, useEffect, useState } from 'react'

export interface IOnboardingState {
  gender?: User.Profile.Gender
  hc_goals?: User.Profile.HcGoals[]
  previous_experience?: User.Profile.PreviousExperience
  age?: number
  height?: number
  weight?: number
  goal_weight?: number
  units?: UnitSystem
  hc_last_ideal_shape?: User.Profile.HcLastIdealShape
  hc_typical_day?: User.Profile.HcTypicalDay
  hc_lifestyle_habits?: User.Profile.HcLifestyleHabits
  hc_commitment?: User.Profile.HcCommitment
  hc_workouts_per_week?: User.Profile.HcWorkoutsPerWeek
  hc_workout_environment?: User.Profile.HcWorkoutEnvironments[]
  hc_why_failed?: User.Profile.HcWhyFailed[]
  hc_motivation?: User.Profile.HcMotivation[]
  hc_coach_skills_focus?: User.Profile.HcCoachSkillsFocus[]
  hc_coach_gender?: User.Profile.HcCoachGender
  hc_coaching_style?: User.Profile.HcCoachingStyle[]
  device_os?: User.Profile.DeviceOS
  device_apple_watch?: boolean
}

export interface IDataState extends IOnboardingState {
  displayName?: string
  email?: string
  coach_id?: string
  coaches?: WithId<Coach>[]
  order_id?: string
}

export const loadingSymbol = Symbol('loading')

export const DataContext = createContext<{
  data: IDataState & { [loadingSymbol]?: true }
  set: (payload: IDataState) => void
  clear: () => void
}>({
  data: {},
  set: () => void 0,
  clear: () => void 0,
})

export function useAppState() {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<IDataState & { [loadingSymbol]?: true }>({
    [loadingSymbol]: true,
  })

  useEffect(() => {
    try {
      const planSession = getItem('session', '@fitify/plan')

      if (planSession) {
        const plan = JSON.parse(planSession)
        setData(plan)
      }
    } catch (err) {
      console.error(err)
    }
    setIsLoading(false)
  }, [])

  const set = (newValue: IDataState) => {
    const planSession = getItem('session', '@fitify/plan')
    const storeData = planSession ? JSON.parse(planSession) : data

    return setData(() => {
      // convert units if necessary
      const result: IDataState = { ...storeData, ...newValue }
      const unitKeys: (keyof Pick<
        IDataState,
        'weight' | 'goal_weight' | 'height'
      >)[] = ['weight', 'goal_weight', 'height']

      if (
        unitKeys.some((key) => result[key] !== null) &&
        result.units === null
      ) {
        throw Error('Need to provide an unit first before setting a value')
      }

      if (result.units && storeData.units && result.units !== storeData.units) {
        const unchangedKeys = unitKeys.filter(
          (key) => storeData[key] !== null && result[key] === storeData[key]
        )

        for (const key of unchangedKeys) {
          let value = result[key]
          if (value !== null && value !== undefined) {
            switch (key) {
              case 'height': {
                if (result.units === UnitSystem.Metric) {
                  value *= CM_PER_INCH
                } else if (result.units === UnitSystem.Imperial) {
                  value /= CM_PER_INCH
                }

                break
              }
              case 'weight':
              case 'goal_weight': {
                if (result.units === UnitSystem.Metric) {
                  value *= KG_PER_LBS
                } else if (result.units === UnitSystem.Imperial) {
                  value /= KG_PER_LBS
                }
                break
              }
              default:
                void 0
            }

            result[key] = Math.round(value)
          }
        }
      }

      // save to session storage if available
      setItem('session', '@fitify/plan', JSON.stringify(result))

      return result
    })
  }

  const clear = () => {
    clearItem('session', '@fitify/plan')
    clearItem('local', '@fitify/subscription/activePromo')

    setData({})
  }

  return { data, set, clear, isLoading }
}
